import { useState } from "react";
import {
  Box,
  Text,
  Group,
  Badge,
  ActionIcon,
  Select,
  TextInput,
  Tooltip,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronRight,
  IconSearch,
  IconX,
  IconFileText,
  IconSettings,
  IconQrcode,
} from "@tabler/icons-react";
import { DataTable } from "../DataTable";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useDebouncedValue } from "@mantine/hooks";
import { useGetGiveawayHistory } from "../../api/giveaways";
import { useSearchParams } from "react-router-dom";
import { ChangesList } from "../Registrations/ChangesList";

const ACTION_CONFIG = {
  update_giveaway: {
    label: "Updated Giveaway",
    color: "cyan",
    icon: IconSettings,
  },
  update_sponsors: {
    label: "Updated Sponsors",
    color: "green",
    icon: IconSettings,
  },
  scan: {
    label: "Scanned",
    color: "violet",
    icon: IconQrcode,
  },
};

const ACTION_OPTIONS = Object.entries(ACTION_CONFIG).map(
  ([value, { label }]) => ({
    value,
    label,
  })
);

dayjs.extend(relativeTime);

function GiveawayHistory({ giveawayId }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [expandedRows, setExpandedRows] = useState([]);

  const page = parseInt(searchParams.get("page") || "1", 10);
  const limit = parseInt(searchParams.get("limit") || "10", 10);

  const filter = (() => {
    try {
      const filterParams = searchParams.get("filter");
      return filterParams ? JSON.parse(filterParams) : null;
    } catch (e) {
      return null;
    }
  })();

  const [debouncedFilter] = useDebouncedValue(filter, 200);

  const queryParams = {
    page,
    limit,
  };

  if (debouncedFilter?.action) {
    queryParams.action = debouncedFilter.action;
  }

  if (debouncedFilter?.user) {
    queryParams.user = debouncedFilter.user;
  }

  const { data, isPending } = useGetGiveawayHistory(giveawayId, queryParams);

  const handlePageChange = (newPage) => {
    setSearchParams((params) => {
      params.set("page", newPage.toString());
      return params;
    });
  };

  const handleFilterChange = (newFilter) => {
    setSearchParams((params) => {
      params.set("page", "1");
      if (!newFilter) {
        params.delete("filter");
      } else {
        params.set("filter", JSON.stringify(newFilter));
      }
      return params;
    });
  };

  return (
    <Box>
      <DataTable
        records={data?.data}
        pagination={{ page, limit }}
        onPageChange={handlePageChange}
        page={page}
        recordsPerPage={limit}
        totalRecords={data?.pagination?.total}
        borderRadius="md"
        verticalSpacing="md"
        rowExpansion={{
          allowMultiple: true,
          expanded: {
            recordIds: expandedRows,
          },
          content: ({ record }) => (
            <Box p="md" bg="gray.0">
              <Text fw={600} size="sm" mb="xs">
                Change Details:
              </Text>
              <ChangesList changes={record.changes} />
            </Box>
          ),
        }}
        columns={[
          {
            accessor: "action",
            width: 240,
            render: ({ action, id }) => {
              const {
                label,
                color,
                icon: Icon,
              } = ACTION_CONFIG[action] ?? {
                label: action,
                color: "gray",
                icon: IconFileText,
              };

              return (
                <Group gap="xs" wrap="nowrap">
                  <ActionIcon
                    variant="light"
                    color="gray"
                    size="sm"
                    onClick={() => {
                      setExpandedRows((rows) =>
                        rows.includes(id)
                          ? rows.filter((row) => row !== id)
                          : [...rows, id]
                      );
                    }}
                  >
                    {expandedRows.includes(id) ? (
                      <IconChevronDown size={14} />
                    ) : (
                      <IconChevronRight size={14} />
                    )}
                  </ActionIcon>
                  <Badge
                    leftSection={<Icon size={16} />}
                    color={color}
                    variant="light"
                  >
                    {label}
                  </Badge>
                </Group>
              );
            },
            filter: (
              <Select
                label="Action"
                description="Filter by action type"
                placeholder="All actions"
                data={ACTION_OPTIONS}
                value={filter?.action}
                onChange={(value) =>
                  handleFilterChange(
                    value ? { ...filter, action: value } : null
                  )
                }
                clearable
              />
            ),
            filtering: filter?.action,
          },
          {
            accessor: "userName",
            title: "User",
            render: ({ userName, userEmail }) => (
              <Group gap="xs" wrap="nowrap">
                <Text size="sm">{userName || userEmail}</Text>
              </Group>
            ),
            filter: (
              <TextInput
                label="User"
                description="Filter by user name or email"
                placeholder="Search user..."
                leftSection={<IconSearch size={16} />}
                rightSection={
                  filter?.user && (
                    <ActionIcon
                      size="sm"
                      variant="transparent"
                      c="dimmed"
                      onClick={() => handleFilterChange(null)}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  )
                }
                value={filter?.user || ""}
                onChange={(e) => {
                  if (e.currentTarget.value === "") {
                    handleFilterChange(null);
                    return;
                  }
                  handleFilterChange({ user: e.currentTarget.value });
                }}
              />
            ),
            filtering: filter?.user,
          },
          {
            accessor: "updatedAt",
            title: "Timestamp",
            render: ({ updatedAt }) => (
              <Tooltip
                label={dayjs(updatedAt).format("DD MMM YYYY - HH:mm:ss")}
              >
                <Text size="sm">{dayjs(updatedAt).fromNow()}</Text>
              </Tooltip>
            ),
          },
          {
            accessor: "changes",
            title: "Changes",
            render: ({ changes }) => {
              const changesCount = changes ? Object.keys(changes).length : 0;
              return (
                <Text size="sm" c="dimmed">
                  {changesCount} {changesCount === 1 ? "field" : "fields"}{" "}
                  changed
                </Text>
              );
            },
          },
        ]}
        fetching={isPending}
      />
    </Box>
  );
}

export default GiveawayHistory;
